import type { Person } from "../models/types";
import { formatLongDate } from "./calendar";

function birthOrderMonth(person: Person): number | null {
  if (!person.birth_month_year) return null;
  const parts = person.birth_month_year.split("-");
  if (parts.length < 2) return null;
  const month = Number(parts[1]);
  return month >= 1 && month <= 12 ? month : null;
}

export function birthOrderSort(a: Person, b: Person): number {
  const ya = a.birth_year ?? Number.MAX_SAFE_INTEGER;
  const yb = b.birth_year ?? Number.MAX_SAFE_INTEGER;
  if (ya !== yb) return ya - yb;
  const ma = birthOrderMonth(a) ?? Number.MAX_SAFE_INTEGER;
  const mb = birthOrderMonth(b) ?? Number.MAX_SAFE_INTEGER;
  if (ma !== mb) return ma - mb;
  const da = a.birth_day ?? Number.MAX_SAFE_INTEGER;
  const db = b.birth_day ?? Number.MAX_SAFE_INTEGER;
  if (da !== db) return da - db;
  return a.display_name.localeCompare(b.display_name, undefined, { sensitivity: "base" });
}

export function currentAge(person: Person): number | null {
  if (person.is_deceased || !person.birth_year) return null;
  const now = new Date();
  let age = now.getFullYear() - person.birth_year;
  const month = birthOrderMonth(person);
  const day = person.birth_day;
  if (month != null) {
    const monthIndex = month - 1;
    const birthdayPassed =
      now.getMonth() > monthIndex ||
      (now.getMonth() === monthIndex && (day == null || now.getDate() >= day));
    if (!birthdayPassed) age -= 1;
  }
  return age;
}

export function ageDescription(person: Person): string | null {
  if (person.is_deceased && person.age_at_death != null) return `${person.age_at_death} yrs`;
  const age = currentAge(person);
  return age != null ? `${age} yrs` : null;
}

export function locationLine(
  person: Person,
  override?: { location_city: string; location_state: string } | null,
): string | null {
  const city = override?.location_city ?? person.location_city;
  const state = override?.location_state ?? person.location_state;
  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  if (state) return state;
  return null;
}

export function residenceLine(
  person: Person,
  override?: { location_city: string; location_state: string } | null,
): string | null {
  const city = override?.location_city ?? person.location_city;
  const state = override?.location_state ?? person.location_state;
  if (city?.trim()) {
    return state?.trim() ? `${city}, ${state}` : city;
  }
  return state?.trim() ?? null;
}

export function memoryLine(person: Person): string | null {
  if (!person.is_deceased) return null;
  if (person.death_date) {
    const iso = person.death_date.slice(0, 10);
    return `In loving memory · ${formatLongDate(iso)}`;
  }
  return "Remembered with love";
}

export function expectingLine(person: Person): string | null {
  if (!person.is_expected) return null;
  const date = person.expected_birth_date;
  if (!date) return "On the way";
  const parts = date.split("-");
  if (parts.length >= 2) {
    const month = Number(parts[1]);
    const year = parts[0] ? Number(parts[0]) : null;
    if (month >= 1 && month <= 12) {
      const monthName = new Date(2000, month - 1, 1).toLocaleString(undefined, { month: "short" });
      return year ? `Due ~${monthName} ${year}` : `Due ~${monthName}`;
    }
  }
  const year = Number(date.slice(0, 4));
  return Number.isFinite(year) ? `Due ~${year}` : "On the way";
}

export function birthOrderRevealLine(person: Person): string | null {
  if (person.birth_month_year) {
    const parts = person.birth_month_year.split("-");
    if (parts.length >= 2) {
      const month = Number(parts[1]);
      const year = Number(parts[0]);
      if (month >= 1 && month <= 12 && Number.isFinite(year)) {
        const monthName = new Date(2000, month - 1, 1).toLocaleString(undefined, { month: "short" });
        return `Born ${monthName} ${year}`;
      }
    }
  }
  if (person.birth_year) return `Born ${person.birth_year}`;
  return null;
}

export function famFocusTitle(branchId: string): string {
  const map: Record<string, string> = {
    dan: "Dan's fam",
    mary_ellen: "Mary Ellen's fam",
    mary_jane: "Jane's fam",
    robert_jr: "Bob's fam",
    kathleen: "Kathleen's fam",
    carl: "Carl's fam",
    mary_ann: "Annsie's fam",
    charles_michael: "Mike's fam",
    mary_margaret: "Peggy's fam",
  };
  return map[branchId] ?? "Sundius family";
}
